import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus, PencilSimple, Trash, MagnifyingGlass } from "@phosphor-icons/react";
import { toast } from "sonner";
import { PageHeader } from "@/components/layout/AppShell";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Icon, AGENT_ICONS } from "@/lib/icons";
import { api, errMsg } from "@/lib/api";

const EMPTY = { name: "", description: "", instructions: "", category: "Personalizada", icon: "Lightning" };

export const SkillDialog = ({ open, onOpenChange, initial, onSaved }) => {
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [loadedFor, setLoadedFor] = useState(null);
  const key = open ? initial?.skill_id || "new" : null;
  if (key !== loadedFor) {
    setLoadedFor(key);
    if (open) setForm(initial ? { ...EMPTY, ...initial } : EMPTY);
  }
  const set = (k) => (e) => setForm({ ...form, [k]: e.target ? e.target.value : e });
  const save = async () => {
    setSaving(true);
    try {
      const payload = { name: form.name, description: form.description, instructions: form.instructions, category: form.category, icon: form.icon };
      const { data } = initial?.skill_id ? await api.put(`/skills/${initial.skill_id}`, payload) : await api.post("/skills", payload);
      toast.success("Skill guardada");
      onSaved?.(data);
      onOpenChange(false);
    } catch (e) { toast.error(errMsg(e)); }
    setSaving(false);
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl rounded-sm border-white/10 bg-[#0f0f0f] text-white" data-testid="skill-dialog">
        <DialogHeader><DialogTitle className="font-display text-2xl">{initial?.skill_id ? "Editar skill" : "Nueva skill personalizada"}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div><label className="label">Nombre</label><input value={form.name} onChange={set("name")} className="field" placeholder="Ej.: Política de reembolsos" data-testid="skill-name-input" /></div>
          <div><label className="label">Descripción corta</label><input value={form.description} onChange={set("description")} className="field" placeholder="Qué aporta esta skill" data-testid="skill-description-input" /></div>
          <div><label className="label">Instrucciones para el agente</label><textarea value={form.instructions} onChange={set("instructions")} rows={6} className="field font-mono !text-xs" placeholder="Describe paso a paso cómo debe actuar el agente cuando use esta skill…" data-testid="skill-instructions-input" /><p className="mt-1 text-xs text-zinc-600">{form.instructions.length}/5000 · mínimo 10 caracteres</p></div>
          <div>
            <label className="label">Icono</label>
            <div className="flex flex-wrap gap-1">
              {AGENT_ICONS.slice(0, 20).map((n) => (
                <button key={n} onClick={() => setForm({ ...form, icon: n })} className={`grid h-9 w-9 place-items-center border ${form.icon === n ? "border-blue-500 bg-blue-600/20 text-white" : "border-white/10 text-zinc-500 hover:text-white"}`} data-testid={`skill-icon-${n}`}><Icon name={n} size={16} /></button>
              ))}
            </div>
          </div>
          <button onClick={save} disabled={saving || !form.name.trim() || form.instructions.trim().length < 10} className="btn-primary w-full" data-testid="skill-save-button">{saving ? "Guardando…" : "Guardar skill"}</button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default function Skills() {
  const qc = useQueryClient();
  const [q, setQ] = useState("");
  const [dialog, setDialog] = useState({ open: false, initial: null });
  const { data } = useQuery({ queryKey: ["skills"], queryFn: async () => (await api.get("/skills")).data });
  const match = (s) => (s.name + s.description + s.category).toLowerCase().includes(q.toLowerCase());
  const remove = async (id) => {
    try { await api.delete(`/skills/${id}`); toast.success("Skill eliminada"); qc.invalidateQueries({ queryKey: ["skills"] }); } catch (e) { toast.error(errMsg(e)); }
  };
  const Tile = ({ s, custom }) => (
    <div className="group flex flex-col bg-[#080808] p-5" data-testid={`skill-tile-${s.skill_id}`}>
      <div className="flex items-start justify-between">
        <Icon name={s.icon} size={22} weight="duotone" className={custom ? "text-cyan-400" : "text-blue-500"} />
        {custom && (
          <div className="flex gap-1 opacity-0 group-hover:opacity-100" style={{ transition: "opacity 160ms" }}>
            <button onClick={() => setDialog({ open: true, initial: s })} className="p-1.5 text-zinc-500 hover:text-white" data-testid={`skill-edit-${s.skill_id}`}><PencilSimple size={15} /></button>
            <button onClick={() => remove(s.skill_id)} className="p-1.5 text-zinc-500 hover:text-red-400" data-testid={`skill-delete-${s.skill_id}`}><Trash size={15} /></button>
          </div>
        )}
      </div>
      <p className="mt-4 text-[10px] uppercase tracking-widest text-zinc-600">{s.category}</p>
      <p className="mt-1 font-semibold">{s.name}</p>
      <p className="mt-1 text-sm text-zinc-500">{s.description}</p>
    </div>
  );
  const custom = (data?.custom || []).filter(match);
  return (
    <div>
      <PageHeader eyebrow="Biblioteca de habilidades" title="Skills" subtitle="Módulos de comportamiento que puedes activar en cualquier agente." actions={<button onClick={() => setDialog({ open: true, initial: null })} className="btn-primary" data-testid="skills-create-button"><Plus size={18} /> Nueva skill</button>} />
      <div className="space-y-10 px-6 py-10 lg:px-12">
        <div className="flex max-w-md items-center gap-3 border border-white/10 bg-[#0d0d0d] px-4"><MagnifyingGlass size={16} className="text-zinc-500" /><input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar skills…" className="h-11 flex-1 bg-transparent text-sm focus:outline-none" data-testid="skills-search-input" /></div>
        <section>
          <p className="mb-4 text-xs font-semibold uppercase tracking-widest text-zinc-500">Mis skills ({custom.length})</p>
          {custom.length ? (
            <div className="grid gap-px border border-white/10 bg-white/10 sm:grid-cols-2 xl:grid-cols-4">{custom.map((s) => <Tile key={s.skill_id} s={s} custom />)}</div>
          ) : <div className="border border-dashed border-white/15 p-8 text-sm text-zinc-500" data-testid="skills-custom-empty">Crea skills con los procesos únicos de tu negocio: políticas, guiones de venta, protocolos…</div>}
        </section>
        <section>
          <p className="mb-4 text-xs font-semibold uppercase tracking-widest text-zinc-500">Predefinidas por HackLabs ({(data?.predefined || []).filter(match).length})</p>
          <div className="grid gap-px border border-white/10 bg-white/10 sm:grid-cols-2 xl:grid-cols-4">{(data?.predefined || []).filter(match).map((s) => <Tile key={s.skill_id} s={s} />)}</div>
        </section>
      </div>
      <SkillDialog open={dialog.open} initial={dialog.initial} onOpenChange={(o) => setDialog((d) => ({ ...d, open: o }))} onSaved={() => qc.invalidateQueries({ queryKey: ["skills"] })} />
    </div>
  );
}
